import React, { useCallback } from 'react';
import { SONG_SLOT_MAX_LENGTH } from '@soundxyz/utils/const';
import { ERROR_TYPE, MINTING_ERROR_ACTIONS, PILLARS } from '@soundxyz/utils/constants/logging';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { Button } from '@components/main-components/button/Button';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext } from '@context/toast';
import { Transaction } from '@context/transaction';
import { useLogError } from '@hooks/useLogErrorNew';
import { usePublishComment } from '@hooks/usePublishComment';
import { useSelectReleaseNft } from '@hooks/useSelectReleaseNft';
import { useSongSlots } from '@hooks/useSongSlots';
import { COMMENT_MAX_CHARS } from '@utils/constants';
import { DataTestId } from '@utils/test';
import { SelectSlotWrapper } from '../CommentV2/SelectSlotWrapper';
import { CommentTextArea } from './CommentTextArea';
import { EditionSelector } from './EditionSelector';
import { ShareToSocialModal } from './ShareToSocial';
import { StatusBar } from './StatusBar';
import { COMMENT_SLOT_ALREADY_TAKEN } from './constants';

export function CommentModalV2({
  releaseId,
  usePurchasedNftInfo,
  editNftId,
}: {
  releaseId: string;
  usePurchasedNftInfo?: boolean;
  editNftId?: string;
}) {
  const logError = useLogError();
  const { tablet } = MediaQuery.useContainer();
  const { closeCancelModal, openModal } = ModalContext.useContainer();
  const { openToast } = ToastContext.useContainer();
  const { purchasedNftInfo } = Transaction.useContainer();

  const {
    loadMore: loadMoreNfts,
    nftsOwnedByUser,
    selectedNft,
    selectedNftIndex,
    setSelectedNftIndex,
    isLoading: isLoadingNft,
  } = useSelectReleaseNft({
    releaseId,
    ...(usePurchasedNftInfo ? { preselectedNftId: purchasedNftInfo?.nftId } : {}),
    ...(editNftId ? { preselectedNftId: editNftId } : {}),
  });
  const {
    claimedSongSlots,
    releaseTrack,
    canOverwriteSlot,
    songSlot,
    setSongSlot,
    isLoading: isLoadingSlot,
  } = useSongSlots({
    releaseId,
    selectedNft,
  });
  const { isLoading, commentText, setCommentText, publishComment, isPublishing } =
    usePublishComment({
      releaseId,
      selectedNft,
      songSlot,
    });

  const disableSlotSelection =
    (typeof selectedNft?.songSlot === 'number' || isPublishing) && !canOverwriteSlot;

  const hasError = React.useMemo(
    () => commentText.length > COMMENT_MAX_CHARS,
    [commentText.length],
  );

  const onSlotClicked = useCallback(
    (slot: number) => {
      if (selectedNft?.songSlot !== slot && claimedSongSlots.has(slot) && !canOverwriteSlot) {
        // this ensure any previous selection is cleared
        setSongSlot(null);
        return openToast({
          text: COMMENT_SLOT_ALREADY_TAKEN,
          variant: 'error',
        });
      }
      return setSongSlot(slot);
    },
    [canOverwriteSlot, claimedSongSlots, openToast, selectedNft?.songSlot, setSongSlot],
  );

  const openShareModal = useStableCallback(() => {
    if (!selectedNft) {
      const message = 'Error skipping commenting from select edition, no nft';
      logError({
        action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
        error: new Error(message),
        errorType: ERROR_TYPE.MISSING_NFT_ID,
        message,
        toast: 'Please refresh and try again',
        level: 'fatal',
        isArtistOnlyPage: false,
        pillar: PILLARS.MINTING,
        indexedTags: {
          releaseId,
        },
      });
      return;
    }

    openModal(ModalType.SHARE_TO_SOCIAL, {
      body: <ShareToSocialModal releaseId={releaseId} nftId={selectedNft.id} />,
    });
  });

  const ctaButtons = (
    <div className="flex w-full justify-end py-1">
      <Button
        fullWidth={!tablet}
        className="mr-2"
        data-testid={DataTestId.SET_TIMESLOT_MODAL_SKIP_BUTTON}
        label="Skip"
        variant="secondary"
        disabled={!selectedNft || isLoading}
        onClick={openShareModal}
      />
      <Button
        fullWidth={!tablet}
        label={isPublishing ? 'Posting...' : 'Comment'}
        variant="primary"
        onClick={publishComment}
        disabled={!selectedNft || hasError}
        loading={isLoading}
      />
    </div>
  );

  if (!releaseTrack) return null;

  const body = (
    <div className="flex flex-col gap-4 md:gap-6">
      <StatusBar releaseId={releaseId} />
      <div className="grid w-full gap-4">
        <div className="text-base-m font-semibold md:text-base-l">Select Edition</div>
        <EditionSelector
          selectedIndex={selectedNftIndex}
          setSelectedIndex={setSelectedNftIndex}
          nfts={nftsOwnedByUser}
          loadMore={loadMoreNfts}
          showCommentedTag
        />
      </div>

      <div className="flex flex-col gap-4">
        <SelectSlotWrapper
          onSlotClicked={onSlotClicked}
          claimedSlots={claimedSongSlots}
          totalSlots={SONG_SLOT_MAX_LENGTH}
          selectedSlot={songSlot}
          trackId={releaseTrack?.id}
          canOverwrite={canOverwriteSlot}
          selectedNft={selectedNft}
          disableSlotSelection={disableSlotSelection}
          loading={isLoadingSlot || isLoadingNft}
        />
        <CommentTextArea
          commentText={commentText}
          setCommentText={setCommentText}
          isLoading={isLoading}
          hasError={hasError}
        />
      </div>
      {ctaButtons}
    </div>
  );

  return (
    <StandardModal
      title="Leave Comment"
      body={body}
      closeModal={closeCancelModal}
      bodyClassName="overflow-y-auto"
      bodyWithoutPaddingBottom
      size="medium"
      isFullScreen={!tablet}
    />
  );
}
